import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import PageHeader from "../../../../components/common/PageHeader";
import PageLoader from "../../../../components/common/PageLoader";
import ChipList from "../../../../components/common/ChipList";
import { tenantUserProfileService } from "../../services";
import {
  formatPasswordExpiryDate,
  formatPasswordExpiryDays,
  mergeUserProfileDetail,
} from "../../utils/normalizeUserProfile";
import UserProfileFormDialog from "../UserProfileFormDialog";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import useAuth from "../../../../hooks/useAuth";

const DetailItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const UserProfileDetailsPage = () => {
  const { profileName: profileParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { activeTenantId, tenantId: authTenantId } = useAuth();
  const tenantId = activeTenantId || authTenantId;
  const listPath = location.pathname.startsWith("/tenant-")
    ? "/tenant-user-profiles"
    : "/user-profiles";

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const loadProfile = useCallback(async () => {
    if (!profileParam) return;
    setLoading(true);
    setError("");
    try {
      const decoded = decodeURIComponent(profileParam);
      const fromState = location.state?.profile || null;
      const seedName = fromState?.profileName || decoded;

      // Details view: load this profile only via GET /userprofile/{name}.
      const detail = await tenantUserProfileService.getTenantUserProfileByName(
        seedName,
        tenantId
      );

      setProfile(
        mergeUserProfileDetail(
          fromState || { profileName: seedName },
          detail
        )
      );
    } catch (err) {
      setProfile(null);
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [location.state, profileParam, tenantId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSave = async (payload) => {
    if (!profile?.profileName) return;
    setSaving(true);
    try {
      await tenantUserProfileService.updateTenantUserProfile(
        profile.profileName,
        payload,
        tenantId
      );
      dispatch(
        appActions.apiSuccessMessage("User profile updated successfully.")
      );
      setEditOpen(false);
      await loadProfile();
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!profile?.profileName) return;
    setSaving(true);
    try {
      await tenantUserProfileService.deleteTenantUserProfile(
        profile.profileName,
        tenantId
      );
      dispatch(
        appActions.apiSuccessMessage("User profile deleted successfully.")
      );
      setDeleteOpen(false);
      navigate(listPath);
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader text="Loading user profile..." />;
  }

  if (error && !profile) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => navigate(listPath)}
        >
          Back to user profiles
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!profile) return null;

  const mfa = profile.mfaConfiguration || { enforcement: "", methods: [] };

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => navigate(listPath)}
        >
          Back to user profiles
        </Button>
      </Stack>
      <PageHeader
        title="User Profile Details"
        subtitle={profile.profileName}
      />

      <Paper
        elevation={0}
        sx={{ p: 3, border: "1px solid", borderColor: "divider", mb: 2 }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DetailItem label="Profile Name" value={profile.profileName} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailItem
                  label="Password Expiry"
                  value={
                    profile.passwordExpiryInSecs != null ||
                    profile.passwordExpiryAt != null
                      ? profile.passwordExpiryAt != null
                        ? formatPasswordExpiryDate(
                            profile.passwordExpiryInSecs,
                            profile.passwordExpiryAt
                          )
                        : `${formatPasswordExpiryDate(
                            profile.passwordExpiryInSecs
                          )} (${formatPasswordExpiryDays(
                            profile.passwordExpiryInSecs
                          )} max age)`
                      : "—"
                  }
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              MFA Configuration
            </Typography>
            <Grid container spacing={2} sx={{ mb: 1.5 }}>
              <Grid item xs={12} md={6}>
                <DetailItem
                  label="Enforcement"
                  value={mfa.enforcement || "Not enabled"}
                />
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary">
              Methods
            </Typography>
            <Box sx={{ mt: 0.75 }}>
              <ChipList
                items={mfa.methods}
                maxVisible={null}
                emptyLabel="No MFA methods configured"
                sx={{ flexWrap: "wrap", rowGap: 0.75, overflow: "visible" }}
              />
            </Box>
          </Box>
        </Stack>
      </Paper>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        flexWrap="wrap"
        useFlexGap
      >
        <Button variant="contained" onClick={() => setEditOpen(true)}>
          Edit Profile
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setDeleteOpen(true)}
        >
          Delete Profile
        </Button>
      </Stack>

      <UserProfileFormDialog
        open={editOpen}
        mode="edit"
        profile={profile}
        loading={saving}
        onClose={() => setEditOpen(false)}
        onSubmit={handleSave}
      />

      <Dialog
        open={deleteOpen}
        onClose={saving ? undefined : () => setDeleteOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete User Profile</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete user profile <strong>{profile.profileName}</strong>? This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProfileDetailsPage;
